import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../../../components/Button';
import Input from '../../../components/Input';
import { INPUT_TYPE_SELECT_SINGLE, INPUT_TYPE_TEXT } from '../../../components/componentConstants';
import { ROUTE_BACK_OFFICE_INSTRUCTION_BLOCKS } from '../../../constants';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';
import { showErrorMessage } from '@/shared/components/toast-system';

const API_BASE = `${SERVER_URL}/api/ui/bo/instruction-blocks`;

const SOURCE_STYLES = {
  base: { label: 'Base Template', color: '#6b7280' },
  common: { label: 'Common Block', color: '#2563eb' },
  scoped: { label: 'Conditional Block', color: '#16a34a' },
  unmatched: { label: 'Unmatched Reference', color: '#dc2626' },
  attachment: { label: 'Auto-Attached', color: '#9333ea' },
  'attachment-note': { label: 'Attaches at Generation Time', color: '#d97706' },
  brain: { label: 'Brand Brain (L3)', color: '#0d9488' },
  'brain-note': { label: 'Brand Brain (L3) — Generation Time', color: '#0d9488' },
};

const ResolvedPrompts = () => {
  const navigate = useNavigate();

  const [meta, setMeta] = useState({ contentTypes: [], verticals: [] });
  const [promptId, setPromptId] = useState('');
  const [masterCompanyTypeId, setMasterCompanyTypeId] = useState('');
  const [intent, setIntent] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const res = await fetch(`${API_BASE}/meta`, { credentials: 'include' });
        if (!res.ok) {
          showErrorMessage('Failed to load reference data.');
          return;
        }
        setMeta(await res.json());
      } catch (error) {
        console.error('ResolvedPrompts: meta load failed', error);
        showErrorMessage('Failed to load reference data.');
      }
    };
    loadMeta();
  }, []);

  const onPreview = async () => {
    if (!promptId) {
      showErrorMessage('Select a content type prompt first.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          promptId,
          masterCompanyTypeId: masterCompanyTypeId ? Number(masterCompanyTypeId) : null,
          intent: intent || null,
        }),
      });
      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        throw new Error(errorBody?.message || 'Failed to build preview.');
      }
      setPreview(await res.json());
    } catch (error) {
      console.error('ResolvedPrompts: preview failed', error);
      showErrorMessage(error?.message || 'Failed to build preview.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="record-form-container">
      <h1>Resolved Prompt Preview</h1>
      <p>
        Shows the exact system prompt sent for a content type, with the base template
        and every instruction block labeled by layer. Templates may place a block
        inline with {'{{block:name}}'}; unreferenced matching blocks are appended.
        Static attachments (humanizer, gallery images) append last, exactly as at
        generation time. Variables like {'{{companyName}}'} resolve at generation time.
      </p>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="input-control separate-label" style={{ minWidth: '280px' }}>
          <label>Content Type Prompt</label>
          <Input
            type={INPUT_TYPE_SELECT_SINGLE}
            label={null}
            name="promptId"
            value={promptId}
            includeNoneOption={true}
            optionsFunction={async () => meta.contentTypes.map((ct) => ({
              value: ct.key,
              label: `${ct.category} — ${ct.name}`,
            }))}
            onChange={setPromptId}
          />
        </div>
        <div className="input-control separate-label" style={{ minWidth: '220px' }}>
          <label>Vertical</label>
          <Input
            type={INPUT_TYPE_SELECT_SINGLE}
            label={null}
            name="masterCompanyTypeId"
            value={masterCompanyTypeId}
            includeNoneOption={true}
            optionsFunction={async () => meta.verticals.map((v) => ({ value: v.id, label: v.name }))}
            onChange={setMasterCompanyTypeId}
          />
        </div>
        <div className="input-control separate-label" style={{ minWidth: '160px' }}>
          <label>Intent</label>
          <Input
            type={INPUT_TYPE_TEXT}
            label={null}
            name="intent"
            value={intent}
            onChange={setIntent}
          />
        </div>
        <Button onClick={onPreview} disabled={loading}>
          {loading ? 'Building...' : 'Preview'}
        </Button>
        <Button className="secondary" onClick={() => navigate(ROUTE_BACK_OFFICE_INSTRUCTION_BLOCKS)}>
          Back to Blocks
        </Button>
      </div>

      {preview && (
        <div style={{ marginTop: '24px' }}>
          <h2>System Prompt (as sent)</h2>
          {preview.segments.filter((s) => s.content).map((segment, idx) => {
            const style = SOURCE_STYLES[segment.source] || SOURCE_STYLES.base;
            return (
              <div
                key={segment.blockId ?? `base-${idx}`}
                style={{
                  borderLeft: `4px solid ${style.color}`,
                  background: 'rgba(0,0,0,0.03)',
                  padding: '12px 16px',
                  marginBottom: '12px',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontSize: '12px', fontWeight: 600, color: style.color, marginBottom: '8px' }}>
                  {style.label}{segment.inline ? ' — placed inline' : ''}: {segment.name}
                </div>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'monospace', fontSize: '13px' }}>
                  {segment.content}
                </pre>
              </div>
            );
          })}

          {preview.user && (
            <>
              <h2>User Prompt</h2>
              <div
                style={{
                  borderLeft: '4px solid #6b7280',
                  background: 'rgba(0,0,0,0.03)',
                  padding: '12px 16px',
                  borderRadius: '4px',
                }}
              >
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'monospace', fontSize: '13px' }}>
                  {preview.user}
                </pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ResolvedPrompts;
